import { MutableRefObject, useEffect, useRef } from 'react';
import { nanoid } from 'nanoid';

/**
 * Identifies one visit — a single entry into an experience.
 *
 * `sessionId` cannot express this: it is minted per browser tab and survives
 * leave/re-enter, so analytics keyed on it folds every re-entry into one. The
 * route param is the signal, because leaving an experience drops it and coming
 * back sets it again, while a GameCast reconnect never touches the route.
 *
 * @param currentEventId - Route param of the open experience.
 * @returns Ref holding the visit id, `null` while no experience is open. A ref
 *   rather than state so a stable callback can read it without being recreated.
 */
export const useVisitId = (
  currentEventId: string | undefined,
): MutableRefObject<string | null> => {
  const visitIdRef = useRef<string | null>(null);

  useEffect(() => {
    visitIdRef.current = currentEventId ? nanoid(20) : null;
  }, [currentEventId]);

  return visitIdRef;
};
